import { secureAuthService } from '@/core/auth/services/SecureAuthService';
import { isSessionInvalidated, resetSessionInvalidated } from '@/api/core/sessionManager';

interface SessionRecoveryOptions {
  /**
   * When true, bypasses the minimum interval guard.
   * Use this for explicit invalidation recovery paths.
   */
  force?: boolean;
  /**
   * If true, recovery only runs when sessionInvalidated is set.
   */
  requireInvalidated?: boolean;
  /**
   * Minimum interval between recovery attempts.
   */
  minIntervalMs?: number;
}

const DEFAULT_MIN_INTERVAL_MS = 5 * 60 * 1000;

let sessionRecoveryPromise: Promise<boolean> | null = null;
let lastSessionRecoveryAt = 0;

function hasAnyAuthCookie(): boolean {
  if (typeof document === 'undefined') return false;
  // Check for cookies that the backend actually sets.
  // csrf-token is the primary indicator (non-httpOnly, always visible to JS).
  // jwt_access and stytch_session are httpOnly so they're invisible to JS,
  // but we check anyway as a fallback in case browser behavior varies.
  return document.cookie.split('; ').some((cookie) =>
    cookie.startsWith('csrf-token=') ||
    cookie.startsWith('jwt_access=') ||
    cookie.startsWith('stytch_session=')
  );
}

/**
 * Shared session recovery path used by both app-level and socket-level listeners.
 * Returns true when validation succeeds.
 */
export async function recoverSessionState({
  force = false,
  requireInvalidated = false,
  minIntervalMs = DEFAULT_MIN_INTERVAL_MS,
}: SessionRecoveryOptions = {}): Promise<boolean> {
  if (typeof window === 'undefined') return false;
  if (!hasAnyAuthCookie()) return false;
  if (requireInvalidated && !isSessionInvalidated()) return false;

  const now = Date.now();
  if (!force && now - lastSessionRecoveryAt < minIntervalMs) {
    return false;
  }

  if (sessionRecoveryPromise) {
    return sessionRecoveryPromise;
  }

  sessionRecoveryPromise = (async () => {
    try {
      const isValid = await secureAuthService.validateSession();
      if (!isValid) return false;

      resetSessionInvalidated();
      lastSessionRecoveryAt = Date.now();
      return true;
    } finally {
      sessionRecoveryPromise = null;
    }
  })();

  return sessionRecoveryPromise;
}
